import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { DatePickerIcon } from '@/assets/static/icons/date-picker';
import { ErrorText } from '@/components/basicComponents/input';
import Label from '@/components/basicComponents/label';
import { formatIsoDateTimes, formatMonthDayYear } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import styled, { css } from 'styled-components';

const getDatePickerSize = (size: DatePickerProps['size'], styleType: DatePickerProps['styleType']) => {
  let paddingRight = '0px';
  let paddingLeft = '0px';

  if (styleType === 'magicWand') {
    paddingLeft = '50px';
  }

  switch (size) {
    case 'sm':
      return css`
        padding: 8px ${`calc(5px + ${paddingRight})`} 8px ${paddingLeft ? `calc(5px + ${paddingLeft})` : '5px'};
        border-radius: 3px;
      `;
    case 'md':
      return css`
        padding: 10px ${`calc(10px + ${paddingRight})`} 10px ${`calc(10px + ${paddingLeft})`};
        border-radius: 4px;
      `;
    case 'lg':
      return css`
        padding: 10px ${`calc(12px + ${paddingRight})`} 10px ${`calc(12px + ${paddingLeft})`};
        border-radius: 5px;
      `;
    default:
      return '';
  }
};

const getDatePickerStyleType = (styleType: DatePickerProps['styleType']) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('layer.1')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.1')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'form':
      return css`
        background-color: ${themeVar('layer.2')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.2')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'success':
      return css`
        background-color: #efffea;
        border: 1px solid ${themeVar('context.success')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #efffea inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'error':
      return css`
        background-color: #ffeaec;
        border: 1px solid ${themeVar('context.error')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #ffeaec inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'disabled':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.disabled')};
      `;
    case 'magicWand':
      return css`
        background: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.base')};

        -webkit-text-fill-color: ${themeVar('font.base')};
      `;
    default:
      return '';
  }
};

const getFontProperties = (size: DatePickerProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        font-size: 13px;
        line-height: 18.2px;
      `;
    case 'md':
      return css`
        font-size: 16px;
        line-height: 22.4px;
      `;
    case 'lg':
      return css`
        font-size: 19px;
        line-height: 26.6px;
      `;
    default:
      return '';
  }
};

const magicWandIconTopPositions = {
  sm: css`
    top: 4px;
  `,
  md: css`
    top: 8px;
  `,
  lg: css`
    top: 11px;
  `
};

interface DatePickerProps {
  value?: string | number;
  onChange?: (string: string) => void;
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'form' | 'success' | 'error' | 'disabled' | 'magicWand';
  width?: string;
  description?: string;
  errorText?: string;
  label?: string;
  tooltip?: string;
  tooltipWidth?: number;
  isRequiredField?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  maxDate?: unknown;
  className?: string;
  ignoreLocalization?: boolean; // if true, the date picker will not be localized
  'data-testid'?: string;
  'data-testid-calendar'?: string;
  'data-testid-label'?: string;
  'data-testid-error'?: string;
}

const DatePicker = ({
  value = '',
  onChange,
  size = 'lg',
  styleType = 'default',
  width = '100%',
  description,
  errorText,
  label,
  tooltip,
  tooltipWidth = 0,
  isRequiredField = false,
  placeholder = 'MM/DD/YYYY',
  autoFocus = true,
  maxDate,
  className,
  ignoreLocalization = false,
  'data-testid': testId,
  'data-testid-calendar': calendarTestId,
  'data-testid-label': labelTestId,
  'data-testid-error': errorTestId
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);

  const handleValue = (date: Dayjs | any) => {
    if (dayjs(date).isValid()) {
      const chosenDate = format(new Date(date.year(), date.month(), date.date()), 'MM/dd/yyyy');
      onChange(chosenDate);
      return;
    }
    onChange(date);
  };

  return (
    <DatePickerWrapper className={className}>
      {label && (
        <Label required={isRequiredField} size={size} tooltip={tooltip} tooltipWidth={tooltipWidth} data-testid={labelTestId}>
          {label}
        </Label>
      )}
      <StyledDesktopDatePickerWrapper>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StyledDesktopDatePicker
            width={width}
            PaperProps={{
              // @ts-ignore - data-testid is not in MUI's PaperProps type but works at runtime
              'data-testid': calendarTestId,
              sx: {
                bgcolor: themeVar('layer.1'),
                color: themeVar('font.strong'),
                border: `1px solid ${themeVar('border.base')}`,
                marginTop: '8px',
                '& .MuiPickersDay-root': {
                  bgcolor: themeVar('layer.2'),
                  color: themeVar('font.strong')
                },
                '& .MuiButtonBase-root': {
                  color: themeVar('font.strong'),
                  '&:hover': {
                    bgcolor: themeVar('action.hover')
                  }
                },
                '& .MuiTypography-root': {
                  color: themeVar('font.weak')
                },
                '& .MuiPickersDay-today': {
                  border: `1px solid ${themeVar('border.base')}`
                },
                '& .PrivatePickersFadeTransitionGroup-root': {
                  '&::-webkit-scrollbar': {
                    width: '10px',
                    left: '-100px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: themeVar('layer.2'),
                    border: '1px solid',
                    borderColor: themeVar('border.base'),
                    padding: '2px',
                    borderRadius: '10px',
                    margin: '6px 0'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: themeVar('layer.4'),
                    backgroundClip: 'padding-box',
                    borderRadius: '10px'
                  }
                }
              }
            }}
            components={{
              OpenPickerIcon: () => <DatePickerIcon fill={themeVar('font.action')} style={{ position: 'absolute', right: '28px' }} />
            }}
            maxDate={maxDate}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            inputFormat="MM/DD/YYYY"
            value={ignoreLocalization ? formatIsoDateTimes(value as string, formatMonthDayYear) : value}
            onChange={(d) => handleValue(d)}
            autoFocus={autoFocus}
            disabled={styleType === 'disabled' || styleType === 'magicWand'}
            renderInput={(params) => {
              const { inputProps, ...rest } = params;
              return <Placeholder {...rest} {...inputProps} styleType={styleType} size={size} placeholder={placeholder} data-testid={testId} />;
            }}
          />
        </LocalizationProvider>
        {styleType === 'magicWand' && (
          <MagicWandIconWrapper size={size}>
            <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
          </MagicWandIconWrapper>
        )}
      </StyledDesktopDatePickerWrapper>
      {description && <Description>{description}</Description>}
      {errorText && <ErrorText size={size} data-testid={errorTestId}>{errorText}</ErrorText>}
    </DatePickerWrapper>
  );
};

export default DatePicker;

const DatePickerWrapper = styled.div`
  width: 100%;
`;

const StyledDesktopDatePickerWrapper = styled.div`
  position: relative;
`;

const StyledDesktopDatePicker = styled(DesktopDatePicker)<{ width: string }>`
  position: relative;
  box-shadow: none;
  width: ${({ width }) => width};

  .MuiInputBase-root {
    padding: 0;
  }
  .MuiOutlinedInput-notchedOutline {
    border: 0;
  }
  .MuiInputAdornment-root {
    margin: 0;
  }
  .MuiInputBase-input {
    height: auto;
  }
`;

const Placeholder = styled(
  ({ styleType, size, placeholder, 'data-testid': testId, ...props }: { styleType: DatePickerProps['size']; size: DatePickerProps['styleType']; placeholder: string; 'data-testid'?: string; inputProps?: any }) => (
    <TextField {...props} placeholder={placeholder} inputProps={{ ...(props.inputProps || {}), 'data-testid': testId }} />
  )
)<{ size: DatePickerProps['size']; styleType: DatePickerProps['styleType'] }>`
  &.MuiTextField-root .MuiOutlinedInput-input {
    font-family: Blinker;

    ${({ size, styleType }) => getDatePickerSize(size, styleType)}
    ${({ styleType }) => getDatePickerStyleType(styleType)}
    ${({ size }) => getFontProperties(size)}
    cursor: ${({ styleType }) => (styleType === 'disabled' ? 'not-allowed' : 'auto')};
  }
`;

const MagicWandIconWrapper = styled.div<{ size: DatePickerProps['size'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 10px;
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};

  ${({ size }) => magicWandIconTopPositions[size]}
`;

const Description = styled.span`
  font-weight: 300;
  font-size: 13px;
  line-height: 18.2px;
  color: ${themeVar('font.weak')};
`;
